// components/Dashboard.jsx
import { useState, useMemo } from 'react';
import { STAGES, dgsCheck, fmtMoney, fmtDate, daysAgo, calcHealthScore } from '../utils/constants';
import { EmptyState, Badge, Icons } from './UI';

const FILTERS = ['All', 'Pre-Solicitation', 'Solicitation', 'Award', 'Execution', 'Active', 'Closed'];
const PHASE_MAP = { 0:0,1:0,2:0,3:0,4:0,5:0, 6:1,7:1,8:1, 9:2,10:2,11:2, 12:3,13:3, 14:4, 15:5 };
const PHASE_LABELS = ['Pre-Solicitation', 'Solicitation', 'Award', 'Execution', 'Active', 'Closed'];

function getStageBadgeColor(stage) {
  const phase = PHASE_MAP[stage];
  return ['gold', 'navy', 'orange', 'teal', 'green', 'gray'][phase] || 'gray';
}

function DaysChip({ date }) {
  const d = daysAgo(date);
  if (d === null) return null;
  const cls = d > 30 ? 'days-alert' : d > 14 ? 'days-warn' : 'days-ok';
  return <span className={`days-badge ${cls}`} style={{ fontSize: 9.5 }}>{d}d</span>;
}

export default function Dashboard({ contracts, openContract, setShowNewModal, setShowImportModal, paal, profile }) {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('All');
  const [filterIT, setFilterIT] = useState('All'); // All | IT | Non-IT
  const [filterMethod, setFilterMethod] = useState(''); // procurement method
  const [sort, setSort] = useState('updated');

  const filtered = useMemo(() => {
    let list = contracts.filter(c => !c.archived);

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(c =>
        (c.title || '').toLowerCase().includes(q) ||
        (c.contractNum || '').toLowerCase().includes(q) ||
        (c.s215?.contractorName || '').toLowerCase().includes(q) ||
        (c.s215?.agreementNum || '').toLowerCase().includes(q) ||
        (c.procMethod || '').toLowerCase().includes(q) ||
        (c.description || '').toLowerCase().includes(q)
      );
    }

    if (filter !== 'All') {
      const phaseIdx = PHASE_LABELS.indexOf(filter);
      list = list.filter(c => PHASE_MAP[c.stage] === phaseIdx);
    }
    if (filterIT === 'IT') list = list.filter(c => c.isIT === 'yes');
    if (filterIT === 'Non-IT') list = list.filter(c => c.isIT !== 'yes');
    if (filterMethod) list = list.filter(c => c.procMethod === filterMethod);

    list.sort((a, b) => {
      if (sort === 'updated')  return (b.updatedAt || '').localeCompare(a.updatedAt || '');
      if (sort === 'created')  return (b.createdAt || '').localeCompare(a.createdAt || '');
      if (sort === 'title')    return (a.title || '').localeCompare(b.title || '');
      if (sort === 'budget')   return (parseFloat(b.budget) || 0) - (parseFloat(a.budget) || 0);
      if (sort === 'stage')    return (b.stage || 0) - (a.stage || 0);
      if (sort === 'health')   { const ha = calcHealthScore(a, paal)?.score ?? 100; const hb = calcHealthScore(b, paal)?.score ?? 100; return ha - hb; }
      return 0;
    });

    return list;
  }, [contracts, search, filter, sort]);

  const procMethods = [...new Set(contracts.filter(c => c.procMethod).map(c => c.procMethod))];

  const needsAttention = contracts.filter(c => {
    const dgs = dgsCheck(c, paal);
    const days = daysAgo(c.stageHistory?.at(-1)?.date);
    return (!c.exemption?.active && dgs.required && c.stage >= 12) ||
           (c.stage === 10 && !c.s215?.unionNotified) ||
           (days !== null && days > 30);
  });

  return (
    <div>
      {/* Page header */}
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">
            Contract Registry
            {needsAttention.length > 0 && (
              <Badge color="red" style={{ fontSize: 10 }}>
                {needsAttention.length} need attention
              </Badge>
            )}
          </h1>
          <p className="page-subtitle">OEIS Admin Services Division · {contracts.length} contract{contracts.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-ghost" onClick={() => setShowImportModal(true)}>
            <Icons.upload /> Import Existing
          </button>
          <button className="btn btn-gold" onClick={() => setShowNewModal(true)}>
            <Icons.plus /> New Contract
          </button>
        </div>
      </div>

      {/* Attention banner */}
      {needsAttention.length > 0 && (
        <div style={{
          background: 'var(--orange-50)', border: '1px solid var(--orange-100)',
          borderRadius: 'var(--r-lg)', padding: '12px 16px', marginBottom: 16,
          display: 'flex', gap: 12, alignItems: 'flex-start',
        }}>
          <Icons.warning />
          <div>
            <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--orange-600)', marginBottom: 4 }}>
              {needsAttention.length} contract{needsAttention.length > 1 ? 's' : ''} need attention
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {needsAttention.slice(0, 3).map(c => (
                <button key={c.id} className="btn btn-xs btn-ghost"
                  style={{ color: 'var(--orange-600)', borderColor: 'var(--orange-100)' }}
                  onClick={() => openContract(c.id)}>
                  {c.title || 'Untitled'}
                </button>
              ))}
              {needsAttention.length > 3 && (
                <span style={{ fontSize: 11, color: 'var(--orange-600)', alignSelf: 'center' }}>
                  +{needsAttention.length - 3} more
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Search + filters */}
      {contracts.length > 0 && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <div className="search-bar">
            <span className="search-bar-icon"><Icons.search /></span>
            <input
              placeholder="Search contracts, contractors, agreement numbers…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          {/* Phase filter pills */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {FILTERS.map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className="btn btn-xs"
                style={{
                  background: filter === f ? 'var(--navy-600)' : 'var(--white)',
                  color: filter === f ? 'var(--white)' : 'var(--slate-600)',
                  borderColor: filter === f ? 'var(--navy-600)' : 'var(--slate-200)',
                }}
              >
                {f}
              </button>
            ))}
          </div>

          {/* IT filter */}
          <select value={filterIT} onChange={e => setFilterIT(e.target.value)}
            style={{ padding: '5px 10px', border: '1.5px solid var(--slate-200)', borderRadius: 'var(--r-md)', fontSize: 12, background: 'var(--white)', color: 'var(--slate-600)', cursor: 'pointer' }}>
            <option value="All">All Types</option>
            <option value="IT">IT Only</option>
            <option value="Non-IT">Non-IT Only</option>
          </select>

          {/* Method filter */}
          <select value={filterMethod} onChange={e => setFilterMethod(e.target.value)}
            style={{ padding: '5px 10px', border: '1.5px solid var(--slate-200)', borderRadius: 'var(--r-md)', fontSize: 12, background: 'var(--white)', color: 'var(--slate-600)', cursor: 'pointer' }}>
            <option value="">All Methods</option>
            {['RFQ','RFO','IFB','NCB','Sole Source','Exempt','Competitive Bid'].map(m => <option key={m} value={m}>{m}</option>)}
          </select>

          {/* Sort */}
          <select value={sort} onChange={e => setSort(e.target.value)}
            style={{ padding: '5px 10px', border: '1.5px solid var(--slate-200)', borderRadius: 'var(--r-md)', fontSize: 12, background: 'var(--white)', color: 'var(--slate-600)', cursor: 'pointer', marginLeft: 'auto' }}>
            <option value="updated">Sort: Last updated</option>
            <option value="created">Sort: Created</option>
            <option value="title">Sort: Title A–Z</option>
            <option value="budget">Sort: Budget ↓</option>
            <option value="stage">Sort: Stage ↓</option>
            <option value="health">Sort: Health ↑ (worst first)</option>
          </select>
        </div>
      )}

      {/* Empty state */}
      {contracts.length === 0 ? (
        <EmptyState
          icon="📋"
          title="No contracts yet"
          subtitle="Create a new contract to get started, or import an existing one from PDF documents."
          action={
            <div className="btn-row" style={{ justifyContent: 'center' }}>
              <button className="btn btn-gold" onClick={() => setShowNewModal(true)}>
                <Icons.plus /> New Contract
              </button>
              <button className="btn btn-ghost" onClick={() => setShowImportModal(true)}>
                <Icons.upload /> Import from PDF
              </button>
            </div>
          }
        />
      ) : filtered.length === 0 ? (
        <EmptyState icon="🔍" title="No results" subtitle="Try adjusting your search or filter." />
      ) : (
        <div>
          {filtered.map(c => {
            const dgs = dgsCheck(c, paal);
            const stageInfo = STAGES[c.stage];
            const lastStageDate = c.stageHistory?.at(-1)?.date;
            const health = calcHealthScore(c, paal);
            const healthColor = !health ? 'var(--slate-300)' : health.score >= 80 ? 'var(--green-500)' : health.score >= 60 ? 'var(--orange-400)' : 'var(--red-500)';

            return (
              <div key={c.id} className="contract-row" onClick={() => openContract(c.id)}>
                <div className="cr-icon">
                  {(c.title || '?').slice(0, 2).toUpperCase()}
                </div>
                <div className="cr-body">
                  <div className="cr-title">{c.title || 'Untitled Contract'}</div>
                  <div className="cr-meta">
                    {c.contractNum ? <><span style={{ fontFamily: 'var(--font-mono)', color: 'var(--navy-500)', fontWeight: 600 }}>{c.contractNum}</span>{' · '}</> : ''}
                    {fmtMoney(c.budget)}
                    {c.procMethod ? ` · ${c.procMethod}` : ''}
                    {c.s215?.contractorName ? ` · ${c.s215.contractorName}` : ''}

                    {' · '}Updated {fmtDate(c.updatedAt)}
                  </div>
                </div>
                <div className="cr-badges">
                  {health && (
                    <div title={'Health: ' + health.score + '/100' + (health.deductions.length > 0 ? ' — ' + health.deductions.join(', ') : '')}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 4, padding: '2px 8px',
                        borderRadius: 99, border: '1.5px solid ' + healthColor,
                        fontSize: 11, fontWeight: 700, color: healthColor, cursor: 'default',
                      }}>
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: healthColor, display: 'inline-block' }} />
                      {health.score}
                    </div>
                  )}
                  <DaysChip date={lastStageDate} />
                  <Badge color={getStageBadgeColor(c.stage)}>
                    {stageInfo?.short || 'Intake'}
                  </Badge>
                  {c.exemption?.active
                      ? <Badge color="green">Exempt</Badge>
                      : dgs.required && (
                          dgs.otaRequired && dgs.olsRequired
                            ? <><Badge color="red">DGS/OTA</Badge><Badge color="orange">DGS/OLS</Badge></>
                            : <Badge color="orange">DGS/OLS</Badge>
                        )
                    }
                  {c.stage === 7 && (!c.dvbe?.solMethod || !c.dvbe?.sbGoal || !c.dvbe?.dvbeRequired) && (
                    <Badge color="orange">SB/DVBE Pending</Badge>
                  )}
                  {c.stage === 10 && !c.s215?.unionNotified && (
                    <Badge color="orange">Union Pending</Badge>
                  )}
                  {c.isShared && (
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 99, background: 'var(--blue-50)', color: 'var(--blue-600)', border: '1px solid var(--blue-100)' }}>
                      👥 Shared
                    </span>
                  )}
                  {c.isIT === 'yes'
                    ? <Badge color="navy">IT</Badge>
                    : <Badge color="gray">Non-IT</Badge>
                  }
                </div>
              </div>
            );
          })}

          <div style={{ fontSize: 11, color: 'var(--slate-400)', textAlign: 'center', marginTop: 8 }}>
            Showing {filtered.length} of {contracts.length} contracts
          </div>
        </div>
      )}
    </div>
  );
}
